import type {
  ApprovalOutcome,
  BlockedField,
  FieldValue,
  PendingChangeDetail,
  PendingChangeSummary,
  PendingClient,
} from '../client';
import { toRecordRow, toValues } from './mapping';
import { ApiError, type Transport } from './transport';
import type { WirePendingChange, WireRecord } from './wire';

/**
 * `PendingClient` over `/api/pending-changes` (INV-10).
 *
 * Thin by design: the server owns every decision (who may approve, whether a proposal is
 * still open, what a locked field does to an approval), so this class maps shapes and
 * nothing else. A client-side "is it still approvable?" check would be a second opinion
 * that goes stale the moment somebody else clicks first.
 */
export class HttpPendingClient implements PendingClient {
  constructor(private readonly transport: Transport) {}

  async list(
    filter: { readonly status?: PendingChangeSummary['status']; readonly objectId?: string } = {},
  ): Promise<readonly PendingChangeSummary[]> {
    const response = await this.transport.request<{
      pendingChanges: readonly WirePendingChange[];
    }>({
      method: 'GET',
      path: '/api/pending-changes',
      query: {
        ...(filter.status === undefined ? {} : { status: filter.status }),
        ...(filter.objectId === undefined ? {} : { objectId: filter.objectId }),
      },
    });
    return response.pendingChanges.map(toPendingChangeSummary);
  }

  async count(): Promise<number> {
    const response = await this.transport.request<{ pending: number }>({
      method: 'GET',
      path: '/api/pending-changes/count',
    });
    return response.pending;
  }

  /** A proposal that is gone (decided and cleaned up, or a stale link) is `null`. */
  async get(id: string): Promise<PendingChangeDetail | null> {
    try {
      const response = await this.transport.request<{
        pendingChange: WirePendingChange;
        current: WireRecord | null;
      }>({ method: 'GET', path: `/api/pending-changes/${id}` });
      return {
        pendingChange: toPendingChangeSummary(response.pendingChange),
        current: response.current === null ? null : toRecordRow(response.current),
      };
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_found') return null;
      throw error;
    }
  }

  async approve(
    id: string,
    values?: Readonly<Record<string, FieldValue>>,
  ): Promise<ApprovalOutcome> {
    const response = await this.transport.request<{
      pendingChange: WirePendingChange;
      record: WireRecord | null;
      blocked: readonly {
        readonly fieldId: string;
        readonly attempted: unknown;
        readonly lock: { readonly userId: string | null; readonly at: string };
      }[];
    }>({
      method: 'POST',
      path: `/api/pending-changes/${id}/approve`,
      body: values === undefined ? {} : { values },
    });

    const record = response.record === null ? null : toRecordRow(response.record);
    const blocked: readonly BlockedField[] = response.blocked.map((entry) => ({
      fieldId: entry.fieldId,
      attempted: entry.attempted as FieldValue,
      // What the server KEPT is what the record it returned holds.
      kept: record?.values[entry.fieldId] ?? null,
      heldBy: { userId: entry.lock.userId ?? '', at: entry.lock.at },
    }));
    return { pendingChange: toPendingChangeSummary(response.pendingChange), record, blocked };
  }

  async reject(id: string, reason: string): Promise<PendingChangeSummary> {
    const response = await this.transport.request<{ pendingChange: WirePendingChange }>({
      method: 'POST',
      path: `/api/pending-changes/${id}/reject`,
      body: { reason },
    });
    return toPendingChangeSummary(response.pendingChange);
  }
}

export function toPendingChangeSummary(change: WirePendingChange): PendingChangeSummary {
  return {
    id: change.id,
    objectId: change.objectId,
    recordId: change.recordId,
    // Absent on every kind but `merge`, where the server's CHECK makes it mandatory.
    loserRecordId: change.mergeLoserRecordId ?? null,
    kind: change.kind,
    status: change.status,
    values: toValues(change.values),
    editedValues: change.editedValues === null ? null : toValues(change.editedValues),
    proposedBy: {
      source: change.proposedBy.source,
      ...(change.proposedBy.context === undefined ? {} : { context: change.proposedBy.context }),
      ...(change.proposedBy.userId === undefined ? {} : { userId: change.proposedBy.userId }),
      ...(change.proposedByName === null ? {} : { displayName: change.proposedByName }),
    },
    decidedBy:
      change.decidedBy === null
        ? null
        : {
            source: change.decidedBy.source,
            ...(change.decidedBy.userId === undefined ? {} : { userId: change.decidedBy.userId }),
            ...(change.decidedByName === null ? {} : { displayName: change.decidedByName }),
          },
    decidedAt: change.decidedAt,
    reason: change.reason,
    createdAt: change.createdAt,
    expiresAt: change.expiresAt,
  };
}
